import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { abbreviate } from '@chainflip/utils/string';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'motion/react';
import { useAccount } from 'wagmi';
import { EmptyFrame } from '@/shared/assets/frames/EmptyFrame';
import { AnimateHeightChange } from '@/shared/components/AnimateHeightChange';
import { Link } from '@/shared/components/atoms/Link';
import { StatsPanel } from '@/shared/components/dashboards/Modal';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import IconButton from '@/shared/components/molecules/IconButton';
import { OperatorLogo } from '@/shared/components/operators/Logos';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import TokenInputField from '@/shared/components/TokenInputField';
import { UsdAmount } from '@/shared/components/UsdAmount';
import WalletActionButton from '@/shared/components/WalletActionButton';
import { useApproveStateChainGateway, useFundAccount } from '@/shared/hooks/funding';
import useApproveScUtils from '@/shared/hooks/useApproveScUtils';
import useAuction from '@/shared/hooks/useAuction';
import useBoolean from '@/shared/hooks/useBoolean';
import useDelegateFlip from '@/shared/hooks/useDelegateFlip';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useRequiredDepositAmount from '@/shared/hooks/useRequiredDepositAmount';
import useSteps, { isStep } from '@/shared/hooks/useSteps';
import useToast from '@/shared/hooks/useToast';
import CheckCircle from '@/shared/icons/flip-ui-kit/large/CheckCircle';
import ChevronIcon from '@/shared/icons/flip-ui-kit/large/Chevron';
import ArrowBackIcon from '@/shared/icons/flip-ui-kit/small/ArrowBack';
import WindowIcon from '@/shared/icons/flip-ui-kit/small/Window';
import { compareDelegationApy } from '@/shared/utils/apy';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { getViemError } from '@/shared/utils/functions';
import { formatBps, formatPct } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';
import OperatorSelectionModal from './OperatorSelectionModal';
import MoneyHand from '../../../assets/svg/hand-with-money.svg';
import { type OperatorSummary } from '../../../hooks/useOperatorsAndAuction';

const steps = ['start', 'approve', 'delegate', 'waiting', 'success'] as const;

type Step = (typeof steps)[number];

const OperatorSelectionButton = ({
  openModal,
  operator,
}: {
  openModal: () => void;
  operator: OperatorSummary | undefined;
}) => (
  <div className="flex flex-col gap-y-2">
    <span className="flex items-center gap-x-2 font-aeonikMedium text-14">
      Operator{' '}
      <QuestionMarkTooltip content="An Operator manages one or more Validators in the network. Delegators stake through Operators to earn rewards." />
    </span>
    <button
      type="button"
      className="flex w-full flex-col gap-y-2 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-13 text-cf-light-2 shadow-grayPop3"
      onClick={openModal}
    >
      <div className="flex w-full items-center justify-between gap-x-4">
        <div className="flex items-center gap-x-1 whitespace-nowrap">
          {operator ? (
            <>
              <OperatorLogo size="small" idSs58={operator.idSs58} />
              <span className="text-cf-white">{operator.alias ?? 'Operator'}</span>
              {abbreviate(operator.idSs58)}
            </>
          ) : (
            'Select an Operator'
          )}
        </div>
        <ChevronIcon orientation="left-right" />
      </div>
    </button>
  </div>
);

const Header = ({
  step,
  amount,
  openModal,
  selectedOperator,
}: {
  step: Step;
  amount: TokenAmount | undefined;
  openModal: () => void;
  selectedOperator: OperatorSummary | undefined;
}) => {
  if (isStep(step, 'waiting', 'success')) {
    let title;

    if (step === 'waiting') {
      title = selectedOperator?.isDelegationAllowed
        ? 'Confirming Delegation'
        : 'Confirming Funding';
    } else if (step === 'success') {
      title = selectedOperator?.isDelegationAllowed ? 'Delegation Complete' : 'Funding Complete';
    }

    return (
      <div className="flex w-full flex-col items-center">
        <div className="relative h-28">
          <div className="absolute left-1/2 h-[120px] -translate-x-1/2">
            <EmptyFrame />
          </div>
          {step === 'waiting' ? (
            <div className="absolute left-1/2 top-[61.5px] -translate-x-1/2">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="absolute left-1/2 top-[60px] -translate-x-1/2">
              <CheckCircle className="scale-150 text-cf-green-1" />
            </div>
          )}
        </div>
        <div className="flex w-full flex-col items-center gap-y-2">
          <span className="font-aeonikMedium text-24 text-cf-white">{title}</span>
          <span className="flex items-center gap-x-1 font-aeonikMedium">
            {amount?.toFormatted()} <TokenWithChainByAsset asset="Flip" /> {FLIP_SYMBOL}
            <span className="text-cf-light-2">
              <UsdAmount token={chainflipAssetMap.Flip} tokenAmount={amount} withParens />
            </span>
          </span>
        </div>
      </div>
    );
  }

  if (step === 'start') {
    return <OperatorSelectionButton openModal={openModal} operator={selectedOperator} />;
  }

  return (
    <>
      <span className="font-aeonikMedium text-24 text-cf-white">
        <div className="flex items-center gap-x-2">
          {selectedOperator?.isDelegationAllowed ? 'Delegate' : 'Fund'} {amount?.toFormatted()}
          <TokenLogo token="Flip" size="medium" />
          {FLIP_SYMBOL}
        </div>
      </span>
      <div className="border-b border-cf-gray-4" />
    </>
  );
};

const RewardsPanel = ({
  operator,
  amount,
  delegatorApy,
}: {
  operator: OperatorSummary | undefined;
  amount: TokenAmount | undefined;
  delegatorApy: number;
}) => (
  <StatsPanel
    items={[
      {
        label: 'Est. APY',
        tooltipContent:
          'The projected annual percentage yield based on current network conditions. Actual returns may vary.',
        content: operator && <span className="text-cf-green-3">{formatPct(delegatorApy)}</span>,
      },
      {
        label: 'Est. Weekly Rewards',
        flipAmount: operator && amount && amount.mul(delegatorApy).div(365).mul(7),
        showPrice: true,
      },
      {
        label: 'Est. Yearly Rewards',
        flipAmount: operator && amount && amount.mul(delegatorApy),
        showPrice: true,
      },
      {
        label: 'Delegation Commission',
        tooltipContent:
          'The percentage of rewards kept by the Operator as commission. The rest is distributed to Delegators.',
        content: operator && <span>{formatBps(operator.nextEpoch.commissionBps)}</span>,
      },
      {
        label: 'Operator',
        tooltipContent:
          'An Operator manages one or more Validators in the network. Delegators stake through Operators to earn rewards.',
        content: operator ? (
          <div className="flex items-center gap-x-1 text-cf-white">
            <OperatorLogo size="small" idSs58={operator.idSs58} />
            <span>{operator.alias ?? 'Operator'}</span>
            <span className="text-cf-light-2">({abbreviate(operator.idSs58)})</span>
            <IconButton
              href={buildChainflipExplorerLink(operator.idSs58, 'operators')}
              target="_blank"
            >
              <WindowIcon />
            </IconButton>
          </div>
        ) : undefined,
      },
    ]}
  />
);

export default function DelegationFlow({
  operators,
  queryParamOperator,
  onComplete,
}: {
  operators: OperatorSummary[];
  queryParamOperator: OperatorSummary | undefined;
  onComplete: () => void;
}) {
  const { step, advanceStep, gotoStep, stepMatches } = useSteps(...steps);
  const router = useRouter();
  const { operator: urlOperator, amount: urlAmount } = router.query;
  const account = useAccount();
  const { value: open, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const { openConnectModal } = useConnectModal();
  const amountFromUrl =
    urlAmount && typeof urlAmount === 'string' && /^\d*\.?\d*$/.test(urlAmount)
      ? TokenAmount.fromWholeUnits(urlAmount, 'Flip')
      : TokenAmount.fromWholeUnits(100, 'Flip');
  const [delegationAmount, setDelegationAmount] = useState<TokenAmount | undefined>(amountFromUrl);
  const [selectedOperator, setSelectedOperator] = useState(queryParamOperator);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (operators.length === 0) return;
    initializedRef.current = true;

    if (queryParamOperator) {
      setSelectedOperator(queryParamOperator);
    } else if (urlOperator && typeof urlOperator === 'string') {
      const op = operators.find((o) => o.idSs58 === urlOperator);
      if (op) {
        setSelectedOperator(op);
      }
    } else {
      const topOp = operators
        .filter((o) => o.isDelegationAllowed)
        .reduce<OperatorSummary | null>(
          (prev, curr) =>
            !prev ||
            compareDelegationApy(curr.nextEpoch.delegatorApy, prev.nextEpoch.delegatorApy) === 1
              ? curr
              : prev,
          null,
        );
      if (topOp) {
        setSelectedOperator(topOp);
      }
    }
  }, [urlOperator, operators, queryParamOperator]);

  const requiredDepositAmount = useRequiredDepositAmount(delegationAmount);
  const toast = useToast('delegation-flow');
  const { auction } = useAuction();

  const timeTillNextEpoch = auction && formatDistanceToNow(auction.endsAt);

  const { delegableBalance, idSs58, evmFlipBalance, nextEpoch } = useEvmScAccount();

  const [error, setError] = useState<string>();

  useEffect(() => {
    setError(undefined);
  }, [delegationAmount]);

  const fundingToast = useToast('funding');
  const {
    mutate: fund,
    isPending: isFunding,
    isSuccess: isFunded,
    reset: resetFundingState,
  } = useFundAccount({
    onMutate: ({ amount }) => {
      fundingToast.loading(
        `Funding account with ${TokenAmount.fromAsset(amount, 'Flip').toFormatted()} ${FLIP_SYMBOL}`,
      );
    },
    onSuccess: ({ amount }) => {
      fundingToast.success(
        `Successfully funded account with ${TokenAmount.fromAsset(amount, 'Flip').toFormatted()} ${FLIP_SYMBOL}`,
      );
      advanceStep();
    },
    onError: (err) => {
      fundingToast.error('Funding failed', { description: getViemError(err) });
    },
  });

  useEffect(() => {
    if (isFunded && delegableBalance?.gt(0)) {
      advanceStep();
      fundingToast.success('Your account has been funded!');
      resetFundingState();
    }
  }, [isFunded, delegableBalance]);

  const { mutate: approveScGateway, isPending: isApprovingScGateway } = useApproveStateChainGateway(
    {
      onMutate: (amount) => {
        fundingToast.loading(
          `Approving ${TokenAmount.fromAsset(amount, 'Flip').toFormatted()} ${FLIP_SYMBOL}...`,
        );
      },
      onSuccess: (result) => {
        if (result === null) {
          fundingToast.success(`Existing ${FLIP_SYMBOL} allowance was high enough`);
        } else {
          fundingToast.success(
            `Successfully approved ${TokenAmount.fromAsset(result.amount, 'Flip').toFormatted()} ${FLIP_SYMBOL}`,
          );
        }
        advanceStep();
      },
      onError: (err) => {
        fundingToast.error('Approval failed', { description: getViemError(err) });
      },
    },
  );

  const { mutate: delegateFlip, isPending: isDelegating } = useDelegateFlip({
    onSubmitted: advanceStep,
    onSuccess: advanceStep,
  });

  const { mutate: approveScUtils, isPending: isApproving } = useApproveScUtils({
    onSuccess: advanceStep,
  });

  let buttonLabel = '';

  if (!account.isConnected) {
    buttonLabel = 'Connect Wallet';
  } else if (stepMatches('start', 'delegate')) {
    buttonLabel =
      selectedOperator?.isDelegationAllowed === false ? 'Fund Account' : `Delegate ${FLIP_SYMBOL}`;
  } else if (step === 'approve') {
    buttonLabel = `Approve ${FLIP_SYMBOL}`;
  } else if (step === 'success' && selectedOperator?.isDelegationAllowed) {
    buttonLabel = 'Go to Dashboard';
  }

  const callout = timeTillNextEpoch && selectedOperator?.isDelegationAllowed && (
    <Callout type="info">
      Your delegated {FLIP_SYMBOL} will start earning rewards in {timeTillNextEpoch} when the next
      epoch starts.{' '}
      <Link
        href="https://docs.chainflip.io/protocol/delegation"
        target="_blank"
        underline
        color="blue"
      >
        Learn more.
      </Link>
    </Callout>
  );

  const operatorApys = useMemo(
    () =>
      new Map(
        operators.map((o) => [o.idSs58, o.nextEpoch.calculateDelegatorApy(delegationAmount)]),
      ),
    [operators, delegationAmount],
  );

  const maxApy = useMemo(() => Math.max(0, ...operatorApys.values()), [operatorApys]);

  return (
    <AnimatePresence mode="wait">
      <motion.div {...defaultAnimationProps} key={step === 'start' ? 'start' : 'other'}>
        {step === 'start' && (
          <div key="hero" className="flex justify-center gap-y-1 pb-8">
            <div className="flex flex-col items-center">
              <div className="mb-[-80px] mt-[-90px]">
                <MoneyHand />
              </div>
              <div className="max-w-[276px] text-center font-aeonikMedium text-24 leading-[36px]">
                <span className="text-white">Delegate {FLIP_SYMBOL} and earn up to </span>
                <span className="text-cf-green-3">{formatPct(maxApy)} APY</span>
              </div>
            </div>
          </div>
        )}
        <div key="content" className="flex w-full flex-col justify-center gap-2 md:flex-row">
          <AnimateHeightChange>
            {stepMatches('approve', 'delegate') && (
              <button
                type="button"
                className="mb-6 inline-flex items-center gap-x-2 text-14 text-cf-light-3"
                onClick={() => gotoStep('start')}
              >
                <ArrowBackIcon />
                <span>Back</span>
              </button>
            )}
            <div className="flex flex-col gap-y-6 rounded-md border border-cf-gray-3-5 bg-cf-gray-3 p-4 md:w-[496px]">
              <Header
                step={step}
                amount={delegationAmount}
                openModal={openModal}
                selectedOperator={selectedOperator}
              />
              {selectedOperator?.openDelegationEnabled === false &&
                (selectedOperator?.isDelegationAllowed ? (
                  step === 'start' && (
                    <Callout type="info">
                      You have been added to the Operator&apos;s whitelist and can delegate.
                    </Callout>
                  )
                ) : (
                  <Callout type="info">
                    {step === 'success'
                      ? 'Your account has been funded. You can tell the Operator to whitelist your Ethereum address.'
                      : "You must be added to the Operator's whitelist after funding your account before you can delegate."}
                  </Callout>
                ))}
              {step === 'start' && (
                <TokenInputField
                  token={chainflipAssetMap.Flip}
                  value={delegationAmount}
                  onChange={setDelegationAmount}
                  balance={evmFlipBalance ?? undefined}
                  accountBalance={
                    nextEpoch?.undelegatedBalance.gt(0) ? nextEpoch.undelegatedBalance : undefined
                  }
                  maxAmount={delegableBalance ?? undefined}
                  error={error}
                  acceptOverflowAmount
                />
              )}
              {stepMatches('waiting', 'success') && callout}
              <RewardsPanel
                operator={selectedOperator}
                amount={delegationAmount}
                delegatorApy={(selectedOperator && operatorApys.get(selectedOperator.idSs58)) ?? 0}
              />
              {stepMatches('start', 'delegate', 'approve') && delegationAmount?.gt(0) && callout}
              {buttonLabel && (
                <WalletActionButton
                  chainId={chainflipAssetMap.Flip.chain.id}
                  requireConnection
                  fullWidth
                  onClick={() => {
                    if (!account.isConnected) {
                      return openConnectModal?.();
                    }

                    if (!delegationAmount || delegationAmount.eq(0)) {
                      return setError('Please enter a valid amount');
                    }

                    if (!selectedOperator) {
                      return toast.error('Please select an Operator');
                    }

                    if (step === 'approve') {
                      if (!selectedOperator.isDelegationAllowed) {
                        return approveScGateway(
                          (requiredDepositAmount ?? delegationAmount).toBigInt(),
                        );
                      }

                      if (!requiredDepositAmount) {
                        advanceStep();
                        return toast.success('On-chain balance is already sufficient');
                      }

                      return approveScUtils(requiredDepositAmount);
                    }

                    if (step === 'delegate') {
                      if (!selectedOperator.isDelegationAllowed) {
                        return fund({
                          accountId: idSs58!,
                          amount: (requiredDepositAmount ?? delegationAmount).toBigInt(),
                        });
                      }

                      return delegateFlip({
                        amount: delegationAmount,
                        operator: selectedOperator.idSs58,
                        depositAmount: requiredDepositAmount,
                      });
                    }

                    if (step === 'start') {
                      return advanceStep?.();
                    }

                    if (step === 'success') {
                      onComplete();
                    }
                  }}
                  loading={
                    (isDelegating || isApproving || isFunding || isApprovingScGateway) && 'iconOnly'
                  }
                  disabled="ifLoading"
                >
                  {buttonLabel}
                </WalletActionButton>
              )}
            </div>
          </AnimateHeightChange>
          <OperatorSelectionModal
            open={open}
            onClose={closeModal}
            setSelectedOperator={setSelectedOperator}
            selectedOperator={selectedOperator}
            delegationAmount={delegationAmount}
            operators={operators}
            allowClosedDelegation
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
